import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle, AlertCircle, User, Mail, Phone, MessageSquare } from 'lucide-react';
import DataProtectionCheckbox from './DataProtectionCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: 'Consulta general',
    mensaje: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSuccess(true);
    } catch (err) {
      if (!err.response) {
        setError('Error de conexión. Compruebe su conexión a internet.');
      } else if (err.response.status === 500) {
        setError('Error del servidor. Inténtelo de nuevo en unos minutos.');
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Error al enviar el mensaje. Inténtelo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-24 px-6 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Info */}
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-600">
              Contacto
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 text-slate-900">
              ¿Necesita asesoramiento especializado?
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              Nuestro equipo de especialistas en derecho laboral y prevención de riesgos está a su disposición para resolver cualquier consulta.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="text-slate-600" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Formulario de contacto</h4>
                  <p className="text-slate-600">Rellene el formulario y le responderemos a la mayor brevedad posible.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-slate-50 p-8 border border-slate-200">
            {success ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle className="mx-auto text-emerald-600 mb-4" size={48} />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">¡Mensaje enviado!</h3>
                <p className="text-slate-600">
                  Nos pondremos en contacto contigo lo antes posible.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Envíenos su consulta</h3>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 flex items-start gap-3">
                    <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Nombre *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        className="form-input pl-10"
                        required
                        data-testid="contact-nombre"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input pl-10"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="form-input pl-10"
                        data-testid="contact-telefono"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Asunto *</label>
                    <select
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="contact-asunto"
                    >
                      <option value="Consulta general">Consulta general</option>
                      <option value="Reincorporación tras baja">Reincorporación tras baja</option>
                      <option value="Asesoramiento SPA">Asesoramiento SPA</option>
                      <option value="Asesoramiento PYME">Asesoramiento PYME</option>
                      <option value="Formación in-company">Formación in-company</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Mensaje *</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 text-slate-400" size={18} />
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      className="form-input pl-10 min-h-[120px] resize-none"
                      required
                      data-testid="contact-mensaje"
                    />
                  </div>
                </div>

                <DataProtectionCheckbox
                  checked={privacyAccepted}
                  onChange={setPrivacyAccepted}
                  id="contact-privacy-consent"
                />

                <button
                  type="submit"
                  disabled={loading || !privacyAccepted}
                  className="btn btn-primary w-full disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      <Send size={18} />
                      Enviar mensaje
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
